import type {PathFunction, PathParams, Query} from './types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

/**
 * Given a path function, a set of parameters for that function, and a query, returns a URL.
 */
export function getUrl<T extends PathParams | void>(path: PathFunction<T>, args: T, query?: Query): URL {
  const pathname = path(args)
  const target = new URL(pathname, ssrSafeLocation.origin || 'https://github.com/')

  if (pathname === ssrSafeLocation.pathname) {
    target.search = new URLSearchParams(ssrSafeLocation.search).toString()
  }

  for (const [key, value] of Object.entries(query || {})) {
    if (value === null || value === undefined) {
      target.searchParams.delete(key)
    } else {
      target.searchParams.set(key, value.toString())
    }
  }

  return target
}

/**
 * Get an href for use in links. This will be a relative URL, and will work properly in SSR.
 */
export function getRelativeHref<T extends PathParams>(path: PathFunction<T>, args: T, query?: Query): string {
  const url = getUrl(path, args, query)
  return url.href.replace(url.origin, '') // strip origin, but keep the rest to make it relative
}

/**
 * Encodes special characters in a URL part, except `/`
 */
export function encodePart(part: string): string {
  return part.split('/').map(encodeURIComponent).join('/')
}

/**
 * Replaces all non-alphanumeric characters with dashes, just like String#parameterize
 * (https://www.rubydoc.info/docs/rails/String:parameterize) in Ruby.
 */
export function parameterize(value: string) {
  return value.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()
}

/**
 * Extracts the sub-path after `/pull/:number` from a pathname.
 * Used to preserve the current tab (e.g. `/changes`, `/commits`) when navigating between stacked PRs.
 *
 * @example
 * getPullRequestTabSuffix('/owner/repo/pull/123/changes') // '/changes'
 * getPullRequestTabSuffix('/owner/repo/pull/123')          // ''
 * getPullRequestTabSuffix('/')                             // ''
 */
export function getPullRequestTabSuffix(pathname: string): string {
  const match = pathname.match(/\/pull\/\d+(\/.*)?$/)
  return match?.[1] ?? ''
}
